/**
 * Slot Check Search: pure view helpers over the GetUniversalSearch response
 * (grouping By Manufacturer on the client, pill colors, map center). No React,
 * no services, so they are unit-tested on their own.
 */
import type {
  IUniversalSearchCasino,
  IUniversalSearchCasinoPin,
  IUniversalSearchEgm,
  IUniversalSearchResponse,
} from 'services/casino';

/** Case- / whitespace-insensitive key (same as the server's group key). */
export function groupKey(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type UniversalEgmWithCasino = IUniversalSearchEgm & {
  casinoId: number;
  casinoName: string;
  metricKey: string;
  /** False: this casino has no Mongo metric source (card without a metric pill). */
  showMetric: boolean;
};

export type UniversalManufacturerGroup = {
  key: string;
  name: string;
  count: number;
  games: number;
  casinos: number;
  egms: UniversalEgmWithCasino[];
};

function metricSort(a: IUniversalSearchEgm, b: IUniversalSearchEgm): number {
  const av = a.metric && typeof a.metric.value === 'number' ? a.metric.value : Number.NEGATIVE_INFINITY;
  const bv = b.metric && typeof b.metric.value === 'number' ? b.metric.value : Number.NEGATIVE_INFINITY;
  return bv - av || a.gameName.localeCompare(b.gameName);
}

export function buildManufacturerGroups(resp: IUniversalSearchResponse | null): UniversalManufacturerGroup[] {
  if (!resp) return [];
  const byKey = new Map<string, UniversalEgmWithCasino[]>();
  for (const c of resp.casinos || []) {
    for (const e of c.egms || []) {
      const k = groupKey(e.manufacturer || 'Unknown');
      const list = byKey.get(k) || [];
      list.push({ ...e, casinoId: c.casinoId, casinoName: c.shortName || c.displayName, metricKey: c.metricKey, showMetric: c.metricAvailable !== false });
      byKey.set(k, list);
    }
  }
  return (resp.manufacturers || []).map((m) => {
    const key = groupKey(m.name);
    return {
      key: `m:${key}`,
      name: m.name,
      count: m.count,
      games: m.games,
      casinos: m.casinos.length,
      egms: (byKey.get(key) || []).sort(metricSort),
    };
  });
}

/** Matching EGM detail ids for one game in one casino (what the casino's own search would select). */
export function gameDetailIds(casino: IUniversalSearchCasino | undefined, gameName: string): string[] {
  if (!casino) return [];
  const k = groupKey(gameName);
  const g = (casino.games || []).find((x) => groupKey(x.name) === k);
  return g ? g.detailIds : [];
}

/** Explore pill gradient per ntile (1–5); null / unknown → gray. */
export const UNIVERSAL_NTILE_GRADIENTS: Record<number, string> = {
  1: 'linear-gradient(83.89deg, #07BDD1 1.68%, #018CE3 97.65%)',
  2: 'linear-gradient(83.89deg, #89DBDD 1.68%, #74C5EF 97.65%)',
  3: 'linear-gradient(84.88deg, #EBEBEB 5.81%, #FFFFFF 95.16%)',
  4: 'linear-gradient(85.08deg, #F485AE 6.1%, #FC4DAD 97.28%)',
  5: 'linear-gradient(85.08deg, #F04288 6.1%, #F60C8D 97.28%)',
};

const NTILE_TEXT: Record<number, string> = { 6: '#FFFFFF', 5: '#FFFFFF', 4: '#FFFFFF', 3: '#636363', 2: '#0B5888', 1: '#05355D' };

export function pillColors(ntile: number | null | undefined): { background: string; color: string } {
  const n = typeof ntile === 'number' ? ntile : NaN;
  if (n >= 1 && n <= 5) return { background: UNIVERSAL_NTILE_GRADIENTS[n], color: NTILE_TEXT[n] };
  return { background: 'rgba(255, 255, 255, 0.18)', color: '#FFFFFF' };
}

/** Map center: the top casino (most matching EGMs) that has coordinates. */
export function topCasinoWithLocation(resp: IUniversalSearchResponse | null): IUniversalSearchCasino | null {
  if (!resp) return null;
  const top = resp.casinos.find((c) => c.casinoId === resp.topCasinoId && c.location);
  return top || resp.casinos.find((c) => !!c.location) || null;
}

/** "12 EGMs in 3 casinos" */
export function resultsSummary(totals: { egms: number; casinos: number } | null | undefined): string {
  if (!totals) return '';
  const e = totals.egms;
  const c = totals.casinos;
  return `${e.toLocaleString()} EGM${e === 1 ? '' : 's'} in ${c} casino${c === 1 ? '' : 's'}`;
}

/** Card meta line: "#1234 · Bank A6H" (display id, never the bare mnum when slotNumber exists). */
export function egmMetaLine(e: Pick<IUniversalSearchEgm, 'displayId' | 'section' | 'bank'>): string {
  const bank = `${e.section || ''}${e.bank || ''}`.trim();
  return [e.displayId ? `#${e.displayId}` : '', bank ? `Bank ${bank}` : ''].filter(Boolean).join(' · ');
}

/**
 * What the map needs per casino. Search results carry the matching-EGM count;
 * the initial (no search yet) pins have count 0 and city/state for the popup.
 */
export type UniversalMapCasino = Pick<IUniversalSearchCasino, 'casinoId' | 'displayName' | 'location' | 'count'> & {
  city?: string;
  state?: string;
};

/** Initial map pins (every casino the search covers) as map casinos with no count. */
export function pinsAsMapCasinos(pins: IUniversalSearchCasinoPin[] | null | undefined): UniversalMapCasino[] {
  return (pins || []).map((p) => ({
    casinoId: p.casinoId,
    displayName: p.displayName,
    location: p.location,
    count: 0,
    city: p.city,
    state: p.state,
  }));
}

/**
 * Casinos the map keeps in view after a search: the top `n` by matching EGMs
 * (with coordinates). The gold #1 (centerCasinoId) always comes first. Ties keep
 * the response order (server ranking).
 */
export function topMapCasinos(
  casinos: UniversalMapCasino[] | null | undefined,
  centerCasinoId: number | null,
  n: number
): UniversalMapCasino[] {
  const withLoc = (casinos || []).filter((c) => !!c.location);
  const ranked = withLoc
    .map((c, i) => ({ c, i }))
    .sort((a, b) => b.c.count - a.c.count || a.i - b.i)
    .map((x) => x.c);
  const top = ranked.find((c) => c.casinoId === centerCasinoId);
  const ordered = top ? [top, ...ranked.filter((c) => c !== top)] : ranked;
  return ordered.slice(0, Math.max(1, Math.floor(n) || 1));
}

/** [[west, south], [east, north]] around every casino with coordinates; null when none. */
export function casinoBounds(
  casinos: UniversalMapCasino[] | null | undefined
): [[number, number], [number, number]] | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const c of casinos || []) {
    if (!c.location) continue;
    w = Math.min(w, c.location.lng);
    e = Math.max(e, c.location.lng);
    s = Math.min(s, c.location.lat);
    n = Math.max(n, c.location.lat);
  }
  if (!Number.isFinite(w)) return null;
  return [
    [w, s],
    [e, n],
  ];
}

/**
 * Map data: GeoJSON points for Mapbox clustering. The top-match casino is left
 * out (always drawn as its own pin, never clustered). Each point carries its
 * casinoId and matching-EGM count; clusters sum the counts (clusterProperties).
 */
export type UniversalMapFeature = {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: { casinoId: number; count: number; name: string };
};

export function casinoMapFeatures(
  casinos: UniversalMapCasino[] | null | undefined,
  excludeCasinoId: number | null
): { type: 'FeatureCollection'; features: UniversalMapFeature[] } {
  const features: UniversalMapFeature[] = [];
  for (const c of casinos || []) {
    if (!c.location || c.casinoId === excludeCasinoId) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [c.location.lng, c.location.lat] },
      properties: { casinoId: c.casinoId, count: c.count, name: c.displayName },
    });
  }
  return { type: 'FeatureCollection', features };
}
